import { Component } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { VendorInvoiceService } from '../../service/vendor-invoice.service';
import { Modal } from 'bootstrap';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import Swal from 'sweetalert2';
import { LoadingService } from '../../service/loading.service';
import { NotificationService } from '../../service/notification.service';
import { UtilityService } from '../../service/utility.service';
import { SessionService } from '../../service/session.service';
import { ConfigService } from '../../service/config.service';

@Component({
  selector: 'app-multiple-invoices',
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule],
  templateUrl: './multiple-invoices.component.html',
  styleUrls: ['./multiple-invoices.component.css'],
})
export class MultipleInvoicesComponent {
  invoiceForm: FormGroup;
  invoices: any[] = [];
  isEditing = false;
  editingIndex = -1;
  submitted = false;
  canSubmit = true;
  purchaseOrdersEnabled = true;

  get isLogedIn(): boolean {
    return this.sessionService.isAuthenticated();
  }

  constructor(
    private fb: FormBuilder,
    private vendorInvoiceService: VendorInvoiceService,
    private router: Router,
    private spinner: LoadingService,
    private notificationService: NotificationService,
    public utilityService: UtilityService,
    private sessionService: SessionService,
    private configService: ConfigService,
  ) {
    this.invoiceForm = this.fb.group({
      IsPo: [true],
      PoNumber: [null],
      OriginalPoAmount: [0.0],
      PoOpenAmount: [0.0],
      ContactName: [null, Validators.required],
      ContactEmail: [null, [Validators.required, Validators.email]],
      InvoiceNumber: [null, Validators.required],
      InvoiceDescription: [
        null,
        [Validators.required, Validators.maxLength(50)],
      ],
      attachments: this.fb.array([]),
      VendorName: [null],
      VendorId: [null],
      InvoiceDate: [null, Validators.required],
    });
    this.invoiceForm.get('IsPo')?.valueChanges.subscribe((isPo) => {
      const poNumberCtrl = this.invoiceForm.get('PoNumber');
      if (isPo) {
        poNumberCtrl?.setValidators(Validators.required);
      } else {
        poNumberCtrl?.clearValidators();
      }
      poNumberCtrl?.updateValueAndValidity();
    });
  }

  ngOnInit(): void {
    this.sessionService.canSubmitInvoice$().subscribe((canSubmit) => {
      this.canSubmit = canSubmit;
    });
    this.configService.whenResolved().subscribe((config) => {
      this.purchaseOrdersEnabled = this.configService.isFeatureEnabled(
        config.purchaseOrdersEnabled,
      );
      if (!this.purchaseOrdersEnabled) {
        this.invoiceForm.get('IsPo')!.setValue(false);
      }
    });
  }

  get filesArray(): FormArray {
    return this.invoiceForm.get('attachments') as FormArray;
  }
  get f() {
    return this.invoiceForm.controls;
  }

  getInvoiceDescriptionCharCount(): number {
    const description = this.invoiceForm.get('InvoiceDescription')?.value;
    return description ? description.length : 0;
  }

  isInvoiceDescriptionLimitExceeded(): boolean {
    return this.getInvoiceDescriptionCharCount() > 50;
  }
  openModal() {
    this.submitted = false;

    this.invoiceForm.reset({
      IsPo: this.purchaseOrdersEnabled,
      PoNumber: null,
      OriginalPoAmount: 0.0,
      PoOpenAmount: 0.0,
      ContactName: null,
      ContactEmail: null,
      InvoiceNumber: null,
      InvoiceDescription: null,
      attachments: [],
      VendorName: null,
      InvoiceDate: null,
    });

    const existingBackdrop = document.querySelector('.modal-backdrop');
    if (existingBackdrop) {
      existingBackdrop.remove();
    }

    const modalElement = document.getElementById('invoiceModal');
    if (modalElement) {
      const modal = new Modal(modalElement);

      modalElement.addEventListener('shown.bs.modal', () => {
        const backdrop = document.querySelector('.modal-backdrop');
        if (backdrop) {
          backdrop.remove();
        }
      });

      modal.show();
    } else {
      console.error('Modal element not found');
    }
  }

  addFile() {
    const fileCount = this.filesArray.length + 1;
    this.filesArray.push(
      new FormControl({
        label: `Additional File ${fileCount}`,
        file: null,
        order: fileCount,
      }),
    );
  }

  onAdditionalFileChanged(file: any, i: number) {
    const currentFile = this.filesArray.at(i).value;
    currentFile.file = file;
    this.filesArray.at(i).patchValue(currentFile);
  }
  closeInvoice() {
    const modalElement = document.getElementById('invoiceModal');
    if (modalElement) {
      const modal = Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }

      const backdrop = document.querySelector('.modal-backdrop');
      if (backdrop) {
        backdrop.remove();
      }
    } else {
      console.error('Modal element not found');
    }

    this.invoiceForm.reset();
    this.clearFiles();
  }
  resetInvoice() {
    this.submitted = false;
    this.invoiceForm.get('IsPo')!.setValue(this.purchaseOrdersEnabled);

    this.clearFiles();

    const existingBackdrop = document.querySelector('.modal-backdrop');
    if (existingBackdrop) {
      existingBackdrop.remove();
    }

    const modalElement = document.getElementById('invoiceModal');
    if (modalElement) {
      const modal = new Modal(modalElement);

      modalElement.addEventListener('shown.bs.modal', () => {
        const backdrop = document.querySelector('.modal-backdrop');
        if (backdrop) {
          backdrop.remove();
        }
      });

      modal.show();
    } else {
      console.error('Modal element not found');
    }
  }

  saveInvoice() {
    if (!this.sessionService.canSubmitInvoice()) {
      this.notificationService.showError(
        'Your session has ended. Please log in again to submit an invoice.',
        'Error',
      );
      this.sessionService.redirectToLogin('/multiple-invoices');
      return;
    }
    this.submitted = true;

    if (this.invoiceForm.invalid) return;

    const invoiceData = this.invoiceForm.value;
    invoiceData.submissionStatus = null;
    invoiceData.attachments = invoiceData.attachments.map((attachment: any) => {
      return attachment || { label: 'Additional File', file: null, order: 0 };
    });

    // Apply login logic to set vendor information if logged in
    if (this.isLogedIn === true) {
      const registrationData = JSON.parse(
        localStorage.getItem('registrationData') || '{}',
      );
      if (registrationData?.name) {
        invoiceData.VendorName = registrationData.name;
        invoiceData.VendorId = registrationData.id;
      }
    }

    // Check PO validation if logged in and it's a PO invoice
    if (
      this.isLogedIn === true &&
      this.invoiceForm.get('IsPo')?.value === true
    ) {
      const poNumber = this.invoiceForm.get('PoNumber')?.value;
      const vendorId = JSON.parse(localStorage.getItem('vendorId') || 'null');

      this.spinner.OnLoading.emit(true);
      this.vendorInvoiceService.checkPoForVendor(poNumber, vendorId).subscribe(
        (data: any) => {
          this.spinner.OnLoading.emit(false);
          if (data) {
            // PO is valid, proceed to save invoice
            this.addInvoiceToArray(invoiceData);
          } else {
            Swal.fire({
              icon: 'error',
              title: 'Error',
              text: 'Warning! The PO number provided does not match this Vendor information. Please review.',
            });
          }
        },
        (err: any) => {
          this.spinner.OnLoading.emit(false);
          Swal.fire({
            icon: 'error',
            title: 'Validation Error',
            text: 'An error occurred while validating the PO.',
          });
          this.notificationService.showError(err, 'Error!');
        },
      );
      return;
    }

    this.addInvoiceToArray(invoiceData);
  }

  private addInvoiceToArray(invoiceData: any) {
    this.invoices.push(invoiceData);

    const modalElement = document.getElementById('invoiceModal');
    if (modalElement) {
      const modal = Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
        const backdrop = document.querySelector('.modal-backdrop');
        if (backdrop) {
          backdrop.remove();
        }
      }
    }

    this.invoiceForm.reset();
    this.clearFiles();
  }

  clearFiles() {
    this.filesArray.clear();
  }

  removeInvoice(index: number) {
    this.invoices.splice(index, 1);
  }

  saveAllInvoices() {
    if (!this.sessionService.canSubmitInvoice()) {
      this.notificationService.showError(
        'Your session has ended. Please log in again to submit an invoice.',
        'Error',
      );
      this.sessionService.redirectToLogin('/multiple-invoices');
      return;
    }
    if (this.invoices.length === 0) {
      this.notificationService.showWarning(
        'Please add atleast one invoice.',
        'Invalid',
      );
      return;
    }

    // Apply login logic to set vendor information if logged in
    if (this.isLogedIn === true) {
      const registrationData = JSON.parse(
        localStorage.getItem('registrationData') || '{}',
      );
      if (registrationData?.name) {
        // Update all invoices with vendor information
        this.invoices.forEach((invoice) => {
          if (!invoice.submissionStatus) {
            invoice.VendorName = registrationData.name;
            invoice.VendorId = registrationData.id;
          }
        });
      }
    }

    this.spinner.OnLoading.emit(true);

    let failedInvoices = []; // To store failed invoices
    let successfulCount = 0; // Track successful invoices

    this.invoices
      .filter((invoice) => !invoice.submissionStatus)
      .forEach((invoice) => {
        this.vendorInvoiceService.createForm([invoice]).subscribe(
          (data) => {
            invoice.submissionStatus = true;
            successfulCount++;
            console.info(data);
            if (
              successfulCount + failedInvoices.length ===
              this.invoices.length
            ) {
              this.spinner.OnLoading.emit(false);
              Swal.fire({
                icon: 'success',
                title: 'Success',
                text: 'All invoices saved successfully!',
              }).then(() => {
                window.location.reload();
              });
            }
          },
          (err: any) => {
            this.spinner.OnLoading.emit(false);
            invoice.submissionStatus = false;
            invoice.submissionError = err;

            // Push failed invoices for consolidated error display. Only surface the
            // backend's own text for expected validation failures (400) so an
            // unrelated 500/503 body doesn't leak internal details to the user.
            failedInvoices.push({
              invoiceNumber: invoice.invoiceNumber || 'Unknown',
              error:
                err?.status === 400 &&
                typeof err.error === 'string' &&
                err.error.trim()
                  ? err.error.trim()
                  : 'An error occurred',
            });

            // Show error message only after all invoices are processed
            if (
              successfulCount + failedInvoices.length ===
              this.invoices.length
            ) {
              this.showErrorMessages(failedInvoices);
            }
          },
        );
      });
  }

  // Function to show consolidated error messages
  showErrorMessages(failedInvoices: any[]) {
    const errorList = failedInvoices
      .map((inv) => `<b>Invoice ${inv.invoiceNumber}:</b> ${inv.error}`)
      .join('<br>');

    Swal.fire({
      icon: 'error',
      title: 'Submission Errors',
      html: `Some invoices failed to submit:<br><br> ${errorList}`,
    });

    // Optional: Show a toast notification for errors
    failedInvoices.forEach((inv) => {
      this.notificationService.showError(
        `Invoice ${inv.invoiceNumber}: ${inv.error}`,
        'Error!',
      );
    });
  }

  async onFileSelected(event: any, fileInput: HTMLInputElement): Promise<void> {
    const overallMaxSize = 45 * 1024 * 1024; // 30MB in bytes
    const selectedFiles = event.target.files;

    if (!selectedFiles) return;

    // Calculate the current total size of selected files
    let currentTotalSize = this.filesArray.controls.reduce(
      (sum, control) => sum + control.value.file.size,
      0,
    );

    for (const file of selectedFiles) {
      if (this.utilityService.isFileEmptyOrCorrupted(file)) {
        this.notificationService.showError(
          this.utilityService.getCorruptedOrEmptyFileMessage(),
          'Invalid File',
        );
        continue;
      }

      if (!this.utilityService.isFileTypeAllowed(file)) {
        this.notificationService.showError(
          this.utilityService.getUnsupportedFileTypeMessage(file.name),
          'Invalid File Type',
        );
        continue;
      }

      if (file.size > overallMaxSize) {
        this.notificationService.showError(
          `"${file.name}" is over the individual file size limit. Please upload a file under 45MB.`,
          'Error',
        );
        continue;
      }

      // Check if adding this file exceeds the total allowed size
      if (currentTotalSize + file.size > overallMaxSize) {
        const remainingSizeMB = (
          (overallMaxSize - currentTotalSize) /
          (1024 * 1024)
        ).toFixed(2);
        this.notificationService.showError(
          `"${file.name}" cannot be added. You can only upload ${remainingSizeMB}MB more.`,
          'Error',
        );
        continue;
      }

      const restrictedZipMessage =
        await this.utilityService.getRestrictedZipContentMessage(file);
      if (restrictedZipMessage) {
        this.notificationService.showError(
          restrictedZipMessage,
          'Invalid File Type',
        );
        continue;
      }

      // Update total size and add the file
      currentTotalSize += file.size;
      const currentFileCount = this.filesArray.controls.length + 1;
      this.filesArray.push(
        new FormControl({
          label: `File ${currentFileCount}`,
          file,
          order: currentFileCount,
        }),
      );
    }

    fileInput.value = ''; // Reset file input
  }

  removeFile(index: number) {
    if (this.filesArray.controls.length > 0) {
      this.filesArray.removeAt(index);
    }
  }

  onIsPoChanged() {
    this.invoiceForm.get('PoNumber')?.setValue('');
  }
}
